import Phaser from "phaser";
import i18next from "i18next";
import { ItemSlot } from "../item-slot";
import { SpriteLoader } from "../../sprite-loader";
import { WindowManager } from "../window-manager";
import { COLORS } from "../colors";
import { BaseWindow } from "./base-window";
import { playSound } from "../../utils/sound-utils";
import { ModernButton } from "../modern-button";
import { Item, ItemDefinition, TILE_SIZE, ItemType } from "../../models/common";
import { kebabToHumanReadable } from "../../utils/strings";

export class MerchantWindow extends BaseWindow {
  private itemInfoContainer: Phaser.GameObjects.Container | null = null;
  private slots: ItemSlot[] = [];
  private buttons: Phaser.GameObjects.Container[] = [];
  private currentMerchantId: number | null = null;

  constructor(scene: Phaser.Scene, windowManager: WindowManager, uiLayer?: Phaser.GameObjects.Container) {
    super(scene, windowManager);
    if (uiLayer) this.setUiLayer(uiLayer);
  }

  getCurrentMerchantId(): number | null {
    return this.currentMerchantId;
  }

  show(
    items: (ItemDefinition & { availableAt?: number })[],
    merchantId: number,
    merchantName: string,
    merchantLevel: number,
    fetchItems: { id: number; count?: number }[],
    fetchItemDefs: ItemDefinition[],
    canUpgrade: boolean,
  ) {
    this.hide();
    this.currentMerchantId = merchantId;

    const baseName = merchantName;

    const baseElements = this.createBaseWindow({
      title: baseName,
      side: "left",
    });

    const bounds = this.getWindowBounds()!;
    const { x, y, width, height } = bounds;

    // (LVL X) label in yellow next to title
    const titleWidth = this.titleText ? this.titleText.width : 0;
    const lvlLabel = this.scene.add.text(
      x + this.CONTENT_PADDING_X + titleWidth + 8,
      y + 25,
      `(LVL ${merchantLevel})`,
      {
        fontSize: "16px",
        color: COLORS.YELLOW,
        fontFamily: "Anton",
        fontStyle: "bold",
      },
    );
    lvlLabel.setOrigin(0, 0.5);
    lvlLabel.setScrollFactor(0);

    // Item info (upper right corner)
    const itemInfo = this.scene.add
      .text(x + width - this.CONTENT_PADDING_X, y + 7, "", {
        fontSize: "16px",
        color: COLORS.YELLOW,
        fontFamily: "Anton",
        align: "right",
      })
      .setOrigin(1, 0);
    itemInfo.setScrollFactor(0);
    const itemTypeInfo = this.scene.add
      .text(x + width - this.CONTENT_PADDING_X, y + 27, "", {
        fontSize: "12px",
        color: "#ffffff",
        fontFamily: "Anton",
        align: "right",
      })
      .setOrigin(1, 0);
    itemTypeInfo.setScrollFactor(0);

    const infoWidth = width - this.CONTENT_PADDING_X * 2;
    const instructionText = this.scene.add.text(
      x + width / 2,
      y + height - 100,
      items.length === 0 ? "Nothing for sale" : "Select an item to buy",
      {
        fontSize: "18px",
        color: COLORS.WHITE,
        fontFamily: "Anton",
        align: "center",
        wordWrap: { width: infoWidth },
      },
    );
    instructionText.setOrigin(0.5, 0);
    instructionText.setScrollFactor(0);


    // Grid setup
    const contentWidth = width - this.CONTENT_PADDING_X * 2;
    const tempSlot = new ItemSlot(0, 0);
    const slotSize = tempSlot.size;
    const gridCols = Math.min(6, Math.max(1, Math.floor(contentWidth / (slotSize + 16))));
    const slotSpacingX = gridCols > 1 ? (contentWidth - slotSize * gridCols) / (gridCols - 1) : 0;
    const slotSpacingY = 8;
    const totalGridWidth = slotSize * gridCols + slotSpacingX * Math.max(0, gridCols - 1);
    const gridStartX = x + width / 2 - totalGridWidth / 2;
    const gridStartY = y + this.CONTENT_PADDING_Y + 20;

    let buyButton: Phaser.GameObjects.Container | null = null;
    let upgradeButton: Phaser.GameObjects.Container | null = null;
    const itemSprites: Phaser.GameObjects.GameObject[] = [];

    // Upgrade slot (green arrow)
    let upgradeSlot: ItemSlot | null = null;
    let redrawUpgradeSlot: ((selected: boolean) => void) | null = null;

    if (canUpgrade) {
      const upgradeSlotIdx = items.length;
      const upgradeRow = Math.floor(upgradeSlotIdx / gridCols);
      const upgradeCol = upgradeSlotIdx % gridCols;
      const upgradeSlotX = gridStartX + upgradeCol * (slotSize + slotSpacingX);
      const upgradeSlotY = gridStartY + upgradeRow * (slotSize + slotSpacingY);

      const slot = new ItemSlot(upgradeSlotX, upgradeSlotY, true);
      upgradeSlot = slot;
      slot.createRectangle(this.scene);

      if (slot.rect && slot.rect instanceof Phaser.GameObjects.Graphics) {
        slot.rect.clear();
        slot.rect.fillStyle(0x1a6b2a, 0.9);
        slot.rect.fillRoundedRect(0, 0, slotSize, slotSize, 6);
        slot.rect.lineStyle(2, 0x2ecc71, 1);
        slot.rect.strokeRoundedRect(0, 0, slotSize, slotSize, 6);
        slot.rect.setInteractive(
          new Phaser.Geom.Rectangle(0, 0, slotSize, slotSize),
          Phaser.Geom.Rectangle.Contains,
        );
      }

      redrawUpgradeSlot = (selected: boolean) => {
        if (slot.rect && slot.rect instanceof Phaser.GameObjects.Graphics) {
          slot.rect.clear();
          slot.rect.fillStyle(0x1a6b2a, 0.9);
          slot.rect.fillRoundedRect(0, 0, slotSize, slotSize, 6);
          slot.rect.lineStyle(selected ? 3 : 2, selected ? 0xffffff : 0x2ecc71, 1);
          slot.rect.strokeRoundedRect(0, 0, slotSize, slotSize, 6);
        }
      };

      const upgradeArrow = SpriteLoader.createSprite(
        this.scene, "items", 0, 2,
        upgradeSlotX + slotSize / 2,
        upgradeSlotY + slotSize / 2,
        true,
      );
      upgradeArrow.displayWidth = slotSize * 0.75;
      upgradeArrow.displayHeight = slotSize * 0.75;
      upgradeArrow.setScrollFactor(0);
      itemSprites.push(upgradeArrow);

      if (slot.rect) {
        slot.rect.on("pointerdown", () => {
          playSound(this.scene, "menu-click-sound", null, 0.5);

          this.slots.forEach((s) => { s.setSelected(false); s.applyBorderStyle(); });
          redrawUpgradeSlot!(true);
          slot.rect?.setInteractive(
            new Phaser.Geom.Rectangle(0, 0, slotSize, slotSize),
            Phaser.Geom.Rectangle.Contains,
          );

          // Clear item info when upgrade slot is selected
          itemInfo.setText("");
          itemTypeInfo.setText("");

          if (this.itemInfoContainer) {
            this.itemInfoContainer.destroy();
            this.itemInfoContainer = null;
          }

          if (fetchItems.length === 0) {
            instructionText.setText(i18next.t("ui:instructions.merchantMaxLevel"));
            return;
          }

          const labelY = y + height - 120;
          instructionText.setText(i18next.t("ui:instructions.upgradeMerchant"));
          instructionText.setPosition(x + width / 2, labelY);
          instructionText.setOrigin(0.5, 0);

          const recipeElements: Phaser.GameObjects.GameObject[] = [];
          const materialStartY = y + height - 70;
          const materialSpacing = 32;
          const materialsPerRow = Math.min(fetchItems.length, 4);
          const slotSpacingUpg = 40 + materialSpacing;
          const contentCenterX = x + this.CONTENT_PADDING_X + (width - this.CONTENT_PADDING_X * 2) / 2;
          const materialStartX = contentCenterX - ((materialsPerRow - 1) * slotSpacingUpg) / 2;

          fetchItems.forEach((fetchItem, idx) => {
            const matItem = fetchItemDefs.find((i) => i.id === fetchItem.id);
            if (!matItem) return;
            const needed = fetchItem.count ?? 1;
            const playerHas = this.countPlayerItem(fetchItem.id);
            const hasEnough = playerHas >= needed;

            const matRow = Math.floor(idx / materialsPerRow);
            const matCol = idx % materialsPerRow;
            const matX = materialStartX + matCol * (40 + materialSpacing);
            const matY = materialStartY + matRow * 60;

            const matBg = this.scene.add.rectangle(matX, matY, 40, 40, hasEnough ? 0x2a2a2a : 0x3a1a1a, 1);
            matBg.setOrigin(0.5);
            matBg.setStrokeStyle(2, 0x666666, 1);
            matBg.setScrollFactor(0);
            recipeElements.push(matBg);

            if (matItem.spriteSheet && typeof matItem.spriteX === "number" && typeof matItem.spriteY === "number") {
              const matSprite = SpriteLoader.createSprite(this.scene, matItem.spriteSheet, matItem.spriteX, matItem.spriteY, matX, matY, true);
              matSprite.displayWidth = TILE_SIZE;
              matSprite.displayHeight = TILE_SIZE;
              matSprite.setScrollFactor(0);
              if (!hasEnough) matSprite.setTint(0xff6666);
              recipeElements.push(matSprite);
            }

            const qtyText = this.scene.add.text(matX + 18, matY - 18, `x${needed}`, {
              fontSize: "14px", color: hasEnough ? "#fff" : COLORS.ORANGE, fontFamily: "Anton", fontStyle: "bold",
            });
            qtyText.setOrigin(0.5);
            qtyText.setScrollFactor(0);
            recipeElements.push(qtyText);

            const countText = this.scene.add.text(matX + 18, matY + 18, `(${playerHas})`, {
              fontSize: "14px", color: hasEnough ? COLORS.GREEN : COLORS.ORANGE, fontFamily: "Anton",
            });
            countText.setOrigin(0.5);
            countText.setScrollFactor(0);
            recipeElements.push(countText);
          });

          this.itemInfoContainer = this.scene.add.container(0, 0, recipeElements);
          this.itemInfoContainer.setScrollFactor(0);
          this.itemInfoContainer.setDepth(201);
          if (this.container) this.container.add(this.itemInfoContainer);

          // Destroy buy button if open
          if (buyButton) {
            if (this.container) this.container.remove(buyButton);
            const idx = this.buttons.indexOf(buyButton);
            if (idx > -1) this.buttons.splice(idx, 1);
            buyButton.destroy();
            buyButton = null;
          }

          // Show UPGRADE button if player has all fetch items
          if (upgradeButton) {
            if (this.container) this.container.remove(upgradeButton);
            const idx = this.buttons.indexOf(upgradeButton);
            if (idx > -1) this.buttons.splice(idx, 1);
            upgradeButton.destroy();
            upgradeButton = null;
          }

          const canUpgradeNow = fetchItems.every(
            (fi) => this.countPlayerItem(fi.id) >= (fi.count ?? 1),
          );
          if (canUpgradeNow) {
            const btnY = y + height - 8;
            const button = new ModernButton({
              scene: this.scene,
              x: x + width / 2,
              y: btnY,
              text: i18next.t("ui:buttons.upgrade"),
              style: "success",
              onClick: () => {
                if (window.socket && merchantId) {
                  window.socket.emit("upgrade-merchant", { merchantId });
                  this.hide();
                }
              },
            });
            upgradeButton = this.scene.add.container(0, 0, [button]);
            upgradeButton.setDepth(100001);
            this.uiLayer.add(upgradeButton);
            this.buttons.push(upgradeButton);
          }
        });
      }
    }

    // Create item slots
    for (let slotIdx = 0; slotIdx < items.length; slotIdx++) {
      const row = Math.floor(slotIdx / gridCols);
      const col = slotIdx % gridCols;
      const slotX = gridStartX + col * (slotSize + slotSpacingX);
      const slotY = gridStartY + row * (slotSize + slotSpacingY);

      const itemDef = items[slotIdx];
      
      // Determine if item is locked (not available at current merchant level)
      const isLocked = (itemDef.availableAt ?? 1) > merchantLevel;
      
      const itemSlot = new ItemSlot(slotX, slotY, true);
      itemSlot.createRectangle(this.scene);
      this.slots.push(itemSlot);

      const playerCredits = this.getPlayerCredits();
      const canAfford = playerCredits >= itemDef.price && !isLocked;

      if (itemDef.spriteSheet && typeof itemDef.spriteX === "number" && typeof itemDef.spriteY === "number") {
        const sprite = SpriteLoader.createSprite(
          this.scene, itemDef.spriteSheet, itemDef.spriteX, itemDef.spriteY,
          slotX + slotSize / 2, slotY + slotSize / 2, true,
        );
        sprite.displayWidth = slotSize * 0.85;
        sprite.displayHeight = slotSize * 0.85;
        if (!canAfford) {
          sprite.setAlpha(0.5);
          sprite.setTint(isLocked ? 0x4444aa : 0x888888);
        }
        itemSprites.push(sprite);
      }

      // Price below slot
      const priceText = this.scene.add
        .text(
          slotX + slotSize / 2,
          slotY + slotSize - 16,
          itemDef.price !== undefined ? `Price: ${itemDef.price}` : "",
          {
            fontSize: "12px",
            color:
              canAfford
                ? "#ffd700"
                : COLORS.ORANGE,
            fontFamily: "Anton",
            align: "center",
          },
        )
        .setOrigin(0.5, 0);
      priceText.setScrollFactor(0);
      itemSprites.push(priceText);

      // Show a small lock badge for locked items
      if (isLocked) {
        const lockBg = this.scene.add.rectangle(slotX + slotSize - 9, slotY + 9, 14, 14, 0x222266, 1);
        lockBg.setScrollFactor(0);
        itemSprites.push(lockBg);
        
        const lockLabel = this.scene.add.text(slotX + slotSize - 9, slotY + 9, "🔒", { fontSize: "9px" });
        lockLabel.setOrigin(0.5).setScrollFactor(0);
        itemSprites.push(lockLabel);
      }

      if (itemSlot.rect) {
        itemSlot.rect.on("pointerdown", () => {
          playSound(this.scene, "menu-click-sound", null, 0.5);

          const currentCredits = this.getPlayerCredits();
          const affordable = currentCredits >= itemDef.price && !isLocked;

          this.slots.forEach((s, idx) => {
            s.setSelected(idx === slotIdx);
            s.applyBorderStyle();
          });
          if (redrawUpgradeSlot) redrawUpgradeSlot(false);

          if (this.itemInfoContainer) {
            this.itemInfoContainer.destroy();
            this.itemInfoContainer = null;
          }

          // Update item info in upper right corner
          itemInfo.setText(itemDef.name);
          const typeStr = itemDef.type === ItemType.Equipment && itemDef.equipType ? itemDef.equipType : itemDef.type;
          itemTypeInfo.setText(typeStr);
          
          // Show lock message if item is locked
          if (isLocked) {
            instructionText.setText(`Trade level ${itemDef.availableAt} required`);
            instructionText.setStyle({ color: COLORS.ORANGE });
          } else if (!affordable) {
            instructionText.setText("NOT ENOUGH CREDITS");
            instructionText.setStyle({ color: COLORS.ORANGE });
          } else {
            instructionText.setText("");
            instructionText.setStyle({ color: COLORS.WHITE });
          }
          this.itemInfoContainer = this.scene.add.container(0, 0, []);
          this.itemInfoContainer.setScrollFactor(0);
          this.itemInfoContainer.setDepth(201);
          if (this.container) this.container.add(this.itemInfoContainer);

          // Remove upgrade button if shown
          if (upgradeButton) {
            this.uiLayer.remove(upgradeButton);
            const idx = this.buttons.indexOf(upgradeButton);
            if (idx > -1) this.buttons.splice(idx, 1);
            upgradeButton.destroy();
            upgradeButton = null;
          }

          // Remove existing buy button
          if (buyButton) {
            this.uiLayer.remove(buyButton);
            const idx = this.buttons.indexOf(buyButton);
            if (idx > -1) this.buttons.splice(idx, 1);
            buyButton.destroy();
            buyButton = null;
          }

          // Show BUY button only if item is not locked
          if (!isLocked && affordable) {
            const btnY = y + height - 8;
            const button = new ModernButton({
              scene: this.scene,
              x: x + width / 2,
              y: btnY,
              text: i18next.t("ui:buttons.buy"),
              style: "success",
              onClick: () => {
                // Get current player credits at time of click
                const currentCredits = this.getPlayerCredits();
                const itemPrice = itemDef.price || 0;

                if (currentCredits >= itemPrice) {
                  if (window.socket && merchantId) {
                    window.socket.emit("buy-merchant-item", {
                      merchantId,
                      itemId: itemDef.id,
                    });
                  }
                }
              },
            });
            buyButton = this.scene.add.container(0, 0, [button]);
            buyButton.setDepth(100001);
            this.uiLayer.add(buyButton);
            this.buttons.push(buyButton);
          }
        });
      }
    }

    this.container = this.scene.add.container(0, 0, [
      ...baseElements,
      lvlLabel,
      itemInfo,
      itemTypeInfo,
      instructionText,
      ...this.slots.map((s) => s.rect).filter((r) => r !== null),
      ...(upgradeSlot !== null && upgradeSlot.rect ? [upgradeSlot.rect] : []),
      ...itemSprites,
      ...this.buttons,
      ...(this.itemInfoContainer ? [this.itemInfoContainer] : []),
    ]);
    this.container.setDepth(200);
    this.visible = true;
    this.uiLayer.add(this.container);
  }

  private getPlayerCredits(): number {
    const player = (window as any).player;
    return player?.data?.credits ?? 0;
  }

  private countPlayerItem(itemId: number): number {
    if (typeof window === "undefined" || !(window as any).player) return 0;
    const player = (window as any).player;
    const inventory: Item[] = player.data.inventory || [];
    const actions: Item[] = player.data.actions || [];
    const lootbags: { items: Item[] }[] = player.data.lootbags || [];
    const storage: Item[] = player.data.storage || [];
    const lootbagItems: Item[] = lootbags.reduce<Item[]>((acc, lb) => acc.concat(lb.items || []), []);
    return (
      inventory.filter((i: Item) => i?.definition?.id === itemId).length +
      actions.filter((i: Item) => i?.definition?.id === itemId).length +
      lootbagItems.filter((i: Item) => i?.definition?.id === itemId).length +
      storage.filter((i: Item) => i?.definition?.id === itemId).length
    );
  }

  hide() {
    if (this.itemInfoContainer) {
      this.itemInfoContainer.destroy();
      this.itemInfoContainer = null;
    }
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
    // Destroy all buttons (BUY and UPGRADE buttons added to uiLayer)
    for (const button of this.buttons) {
      if (button) {
        button.destroy();
      }
    }
    this.slots = [];
    this.buttons = [];
    this.currentMerchantId = null;
    this.visible = false;
  }

  isVisible(): boolean {
    return this.visible;
  }
}
